'use client'

import React, { useState } from 'react'
import { createPortal } from 'react-dom'

const ONBOARD_CLIENT_MODAL_Z_CLASS = 'z-[150]'

interface OnboardWithClientModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function OnboardWithClientModal({ isOpen, onClose, onSuccess }: OnboardWithClientModalProps) {
  const [clientCode, setClientCode] = useState('')
  const [details, setDetails] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isValidCode = clientCode.length === 6 && /^[A-Z]{6}$/.test(clientCode)
  const isValidDetails = details.trim().length > 0
  const isValidSubmission = isValidCode && isValidDetails

  const handleCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6)
    setClientCode(value)
    setError('')
    setSuccess('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isValidSubmission) {
      setError('Please enter a valid 6-letter client code and provide details')
      return
    }

    try {
      setIsLoading(true)
      setError('')
      setSuccess('')

      // Get token with fallback pattern like other components
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();
      
      if (!token) {
        setError('Authentication required. Please log in again.')
        return
      }

      // Use Next.js API directly since we implemented it there
      const response = await fetch('/api/v1/onboarding/redeem', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: clientCode,
          details: details.trim()
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        const message = `${result.message || 'Successfully submitted join request!'} Reference #${result.ref_id}`
        setSuccess(message)
        setTimeout(() => {
          onSuccess()
          onClose()
          // Refresh the page to update navigation
          window.location.reload()
        }, 3000) // Extended to 3 seconds to allow reading the reference number
      } else {
        setError(result.error || 'Failed to join client')
      }
    } catch (err: unknown) {
      console.error('Error submitting join request:', err)
      setError(err instanceof Error ? err.message : 'Failed to request client access')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setClientCode('')
    setDetails('')
    setError('')
    setSuccess('')
    setIsLoading(false)
    onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center ${ONBOARD_CLIENT_MODAL_Z_CLASS} p-4`}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Onboard With Client</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Info Section */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Join Your Organization</h3>
            <p className="text-gray-600 text-sm">
              Enter the 6-letter client code provided by your client administrator.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client Code Input */}
            <div>
              <label htmlFor="clientCode" className="block text-sm font-medium text-gray-700 mb-2">
                Client Code
              </label>
              <input
                id="clientCode"
                type="text"
                value={clientCode}
                onChange={handleCodeInput}
                placeholder="e.g. TECH01"
                maxLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono tracking-wider uppercase"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter 6 capital letters (A-Z)
              </p>
            </div>

            {/* Details Input */}
            <div>
              <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
                Details <span className="text-red-500">*</span>
              </label>
              <textarea
                id="details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Please provide details about your role, department, or reason for joining this client..."
                rows={4}
                maxLength={1000}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                {details.length}/1000 characters
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-700 text-sm">{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isValidSubmission || isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Join Client'
              )}
            </button>
          </form>

          {/* Help Section */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>Don't have a client code?</strong><br />
                  Contact your client administrator or property manager to get your unique 6-letter client code.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent
}
